'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { JobDetail } from '~/types/job-detail';
import { addFavorite, removeFavorite } from '~/app/actions/favorites-action';
import { setCookieAction } from '~/app/actions/client/set-cookie-action';
import { getCookieAction } from '~/app/actions/client/get-cookie-action';

interface ActionButtonsProps {
  job: JobDetail;
  isSignedIn: boolean;
  jobApplicantsJobIds: number[];
  isMobile: boolean;
  isFavorite: boolean;
  favoriteJobIds: number[];
  setIsFavorite: (isFavorite: boolean) => void;
}

export function ActionButtons({
  job,
  isSignedIn,
  jobApplicantsJobIds,
  isMobile,
  isFavorite,
  favoriteJobIds,
  setIsFavorite,
}: ActionButtonsProps) {
  useEffect(() => {
    if (isSignedIn) {
      setIsFavorite(favoriteJobIds.includes(job.id));
    } else {
      (async () => {
        const favoriteJobIdsFromCookie = JSON.parse(
          (await getCookieAction('favorite_job_ids')) || '[]',
        );
        setIsFavorite(favoriteJobIdsFromCookie.includes(job.id));
      })();
    }
  }, [job.id, favoriteJobIds, isSignedIn]);

  const currentApplicantJobIds: number[] = jobApplicantsJobIds;

  const handleFavoriteToggle = async () => {
    if (isSignedIn) {
      // ログインしている場合はDBで管理
      if (isFavorite) {
        // お気に入り登録済みの場合は削除
        setIsFavorite(false);
        await removeFavorite(job.id);
      } else {
        // お気に入り登録未済の場合は追加
        setIsFavorite(true);
        await addFavorite(job.id);
      }
    } else {
      // ログインしていない場合はクッキーで管理
      const favorite_job_ids = JSON.parse(
        (await getCookieAction('favorite_job_ids')) || '[]',
      );
      if (isFavorite) {
        setIsFavorite(false);
        setCookieAction({
          key: 'favorite_job_ids',
          value: JSON.stringify(
            favorite_job_ids.filter((id: number) => id !== job.id),
          ),
        });
      } else {
        setIsFavorite(true);
        setCookieAction({
          key: 'favorite_job_ids',
          value: JSON.stringify([...favorite_job_ids, job.id]),
        });
      }
    }
  };

  const position = 'center';
  const serviceType = job.service_type == '求人広告' ? 'ad' : 'agent';

  const default_text = isMobile ? 'お気に入り' : 'お気に入り登録';
  const done_text = isMobile ? 'お気に入り' : 'お気に入り登録済';

  return (
    <div className="p-job-detail__buttons">
      {!job.closing &&
        (isSignedIn ? (
          <>
            <button
              onClick={handleFavoriteToggle}
              className={classNames(
                'c-button -border-orange -heart-icon -sp-text-sm',
                isFavorite && '-solid -done',
                isMobile ? 'u-w150' : '-w280',
              )}
            >
              {isFavorite ? done_text : default_text}
            </button>

            {currentApplicantJobIds.includes(job.id) ? (
              <div
                className={classNames(
                  'c-button -lightgray -double u-mr15 -disabled',
                  isMobile ? 'u-w150' : '-w280',
                )}
              >
                応募済み
              </div>
            ) : (
              <Link
                href={`/mypage/jobs/new?job_id=${job.id}&ref=application_${position}_${serviceType}`}
                className={classNames(
                  'c-button -red -double -arrow-right',
                  isMobile ? 'u-w150 u-pr8' : '-w280',
                )}
              >
                応募画面へ進む
              </Link>
            )}
          </>
        ) : serviceType === 'agent' ? (
          <>
            <button
              onClick={handleFavoriteToggle}
              className={classNames(
                'c-button -border-orange -heart-icon -sp-text-sm',
                isFavorite && '-done -solid',
                isMobile ? 'u-w50 u-fs-sp8 u-flex -column' : 'u-w280',
              )}
            >
              {isFavorite ? done_text : default_text}
            </button>
            <Link
              href={`/entry_member?job_id=${job.id}&ref=inquiry_${position}_agent`}
              className={classNames(
                'c-button -green -double -arrow-right',
                isMobile ? 'u-w120 u-fs10 u-pr8' : 'u-w280',
              )}
            >
              {isMobile ? '求人を問い合わせる' : 'この求人を問い合わせる'}
            </Link>
            <Link
              href={`/entry_member?job_id=${job.id}&ref=application_${position}_agent`}
              className={classNames(
                'c-button -red -double -arrow-right',
                isMobile ? 'u-w120 u-fs11 u-pr8' : 'u-w280',
              )}
            >
              応募画面へ進む
              <span className="c-button__micro_copy">1分かんたん応募</span>
            </Link>
          </>
        ) : (
          <>
            <button
              onClick={handleFavoriteToggle}
              className={classNames(
                'c-button -border-orange -heart-icon -sp-text-sm',
                isFavorite && '-done -solid',
                isMobile ? 'u-w150' : 'u-w280',
              )}
            >
              {isFavorite ? done_text : default_text}
            </button>
            <Link
              href={`/entry_member?job_id=${job.id}&ref=application_${position}_ad`}
              className={classNames(
                'c-button -red -double -arrow-right',
                isMobile ? 'u-w150 u-pr8' : '-w280',
              )}
            >
              応募画面へ進む
              <span className="c-button__micro_copy">1分かんたん応募</span>
            </Link>
          </>
        ))}
    </div>
  );
}
